import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type BestsellersProps = {
  language: Language;
};
const filters = {
  ua: ['Усі', 'Аерозолі', 'Грунти', 'Захисні покриття', 'Автокосметика'],
  en: ['All', 'Aerosols', 'Primers', 'Protective Coatings', 'Auto Cosmetics']
};
const products = {
  ua: [{
    id: 1,
    name: '2K Top Coat Acryl',
    benefit: 'Преміальна двокомпонентна емаль',
    category: 'Захисні покриття',
    image: 'https://cdn.magicpatterns.com/uploads/qxm3nLwmuwQQabzX6hsntH/2K_Top_coat2B260_hardener.png'
  }, {
    id: 2,
    name: '1K Acryllack',
    benefit: 'Швидкосохнуча акрилова емаль',
    category: 'Аерозолі',
    image: 'https://cdn.magicpatterns.com/uploads/h5JLmxF7acwLetAJnog3ov/001_002.png'
  }, {
    id: 3,
    name: 'Basecoat',
    benefit: 'Базова емаль високої покриваності',
    category: 'Захисні покриття',
    image: 'https://cdn.magicpatterns.com/uploads/tusuHWMBht1toW8wtMHRQv/Base_coat.png'
  }, {
    id: 4,
    name: 'Basecoat Binder',
    benefit: 'Біндер для базової емалі',
    category: 'Грунти',
    image: 'https://cdn.magicpatterns.com/uploads/mpZYZpDnHbHKYhsWSitUZF/BC_Binder.png'
  }],
  en: [{
    id: 1,
    name: '2K Top Coat Acryl',
    benefit: 'Premium two-component enamel',
    category: 'Protective Coatings',
    image: 'https://cdn.magicpatterns.com/uploads/qxm3nLwmuwQQabzX6hsntH/2K_Top_coat2B260_hardener.png'
  }, {
    id: 2,
    name: '1K Acryllack',
    benefit: 'Fast-drying acrylic enamel',
    category: 'Aerosols',
    image: 'https://cdn.magicpatterns.com/uploads/h5JLmxF7acwLetAJnog3ov/001_002.png'
  }, {
    id: 3,
    name: 'Basecoat',
    benefit: 'High coverage base enamel',
    category: 'Protective Coatings',
    image: 'https://cdn.magicpatterns.com/uploads/tusuHWMBht1toW8wtMHRQv/Base_coat.png'
  }, {
    id: 4,
    name: 'Basecoat Binder',
    benefit: 'Binder for base enamel',
    category: 'Primers',
    image: 'https://cdn.magicpatterns.com/uploads/mpZYZpDnHbHKYhsWSitUZF/BC_Binder.png'
  }]
};
const sectionText = {
  ua: {
    title: 'Бестселери',
    cta: 'Детальніше',
    viewAll: 'Переглянути всі продукти'
  },
  en: {
    title: 'Bestsellers',
    cta: 'Learn More',
    viewAll: 'View All Products'
  }
};
export function Bestsellers({
  language
}: BestsellersProps) {
  const [activeFilter, setActiveFilter] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const filterList = filters[language];
  const productList = products[language];
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-1">
      {/* Subtle background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.05] pointer-events-none" style={{
      background: 'radial-gradient(circle, #FFB81C 0%, transparent 70%)'
    }} data-id="element-2" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16" data-id="element-4">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-5">
            {t.title}
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-6" />
        </motion.div>

        {/* Filters - Desktop only */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="hidden lg:flex flex-wrap justify-center gap-3 mb-12" data-id="element-7">
          {filterList.map((filter, index) => <button key={filter} onClick={() => setActiveFilter(index)} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeFilter === index ? 'bg-chamaleon-yellow text-chamaleon-black' : 'bg-transparent border border-white/20 text-gray-400 hover:text-white hover:border-chamaleon-yellow'}`} data-id="element-8">
              {filter}
            </button>)}
        </motion.div>

        {/* Products Grid - 4 items */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-9">
          {productList.map((product, index) => <motion.div key={product.id} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + index * 0.1
        }} whileHover={{
          y: -8
        }} className="group" data-id="element-10">
              <div className="glass-card rounded-xl sm:rounded-2xl overflow-hidden h-full flex flex-col border border-white/5 hover:border-white/10 transition-colors" data-id="element-11">
                {/* Product Image Area */}
                <div className="relative h-56 sm:h-64 bg-[#050505] overflow-hidden flex items-center justify-center" data-id="element-12">
                  {/* Subtle glow behind the product on hover */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-chamaleon-yellow/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-13" />

                  {/* Product Image with screen blend mode to hide black background */}
                  <motion.img src={product.image} alt={product.name} className="relative z-10 w-full h-full object-contain p-4 mix-blend-screen" whileHover={{
                scale: 1.08
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-14" />

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-chamaleon-yellow/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-15" />
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6 flex-grow flex flex-col bg-white/[0.02]" data-id="element-16">
                  <div className="text-xs font-medium text-chamaleon-yellow mb-2 uppercase tracking-wider" data-id="element-17">
                    {product.category}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 transition-colors" data-id="element-18">
                    {product.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-6 leading-relaxed flex-grow" data-id="element-19">
                    {product.benefit}
                  </p>
                  <motion.a href="#" className="inline-flex items-center gap-2 text-white font-medium text-sm group/link hover:text-chamaleon-yellow transition-colors" whileHover={{
                x: 5
              }} data-id="element-20">
                    {t.cta}
                    <ArrowRightIcon className="w-4 h-4 text-chamaleon-yellow group-hover/link:translate-x-1 transition-transform" data-id="element-21" />
                  </motion.a>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* View All Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="text-center mt-4" data-id="element-22">
          <motion.a href="#products" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 hover:border-chamaleon-yellow transition-all duration-300 text-sm sm:text-base" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-23">
            {t.viewAll}
            <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-24" />
          </motion.a>
        </motion.div>
      </div>
    </section>;
}